import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Button,
    IconButton,
    Paper,
    Stack,
    Box,
    Alert,
    TextField,
    Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import BlockIcon from "@mui/icons-material/Block";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { postBlacklistDomainEndpoint } from "../../../common/endpoints";
import { postBlacklistDomainSlice } from "../../../redux/actions/postBlacklistDomainSlice";

const BlacklistDomainModal = ({
    open,
    handleClose,
    domains,
    requestedBy,
    onSubmit,
}) => {

    const [reason, setReason] = useState("Business Purpose. These are the domains which will not be allowed to access Supplier forecast Collaboration application.");
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const submit = async () => {

        try {
            setLoading(true);

            const payload = {
                domains: domains?.map((d) => d.domain) || [],
                user: requestedBy,
                justification: reason,
            };

            const response = await dispatch(
                postBlacklistDomainSlice({
                    dynamicEndpoint: postBlacklistDomainEndpoint,
                    data: payload,
                })
            );

            if (response?.meta?.requestStatus === "fulfilled") {

                const result = response?.payload;

                const acceptedDomains = result?.acceptedDomains || [];
                const invalidDomains = result?.invalidDomains || [];

                let toastMessage = result?.message || "Request submitted successfully.";

                if (acceptedDomains.length > 0) {
                    toastMessage += `\n\nAccepted Domains:\n${acceptedDomains.join(", ")}`;
                }

                if (invalidDomains.length > 0) {
                    toastMessage += `\n\nInvalid Domains:\n${invalidDomains.join(", ")}`;
                }

                toast.success(toastMessage, {
                    autoClose: 7000,
                });

                if (onSubmit) {
                    onSubmit(result);
                }

                setReason("");
                handleClose();

            } else {
                toast.error("Failed to blacklist domains.");
            }

        } catch (error) {
            console.error("Blacklist API error:", error);
            toast.error("Something went wrong.");
        } finally {
            setLoading(false);
            handleClose()
        }
    };

    const onClose = () => {
        setReason("");
        handleClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontWeight: 700,
                }}
            >
                Remove Whitelist

                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>

            </DialogTitle>
            <DialogContent>
                <Typography
                    color="text.secondary"
                    mb={2}
                >
                    The selected domains will be removed from the whitelist. This action will update their status Inactive
                </Typography>
                <Alert
                    severity="warning"
                    icon={<WarningAmberRoundedIcon />}
                    sx={{ mb: 3 }}
                >
                    Removing a domain from whitelist will revoke supplier collaboration
                    access. Please ensure this is intentional before proceeding.
                </Alert>
                <Typography
                    fontWeight={700}
                    mb={1}
                >
                    DOMAINS TO BE REMOVED FROM WHITELIST({domains.length})
                </Typography>

                <Paper variant="outlined">
                    {domains.map((item) => (
                        <Stack
                            key={item.domain}
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{
                                px: 2,
                                py: 1.5,
                                borderBottom: "1px solid #eee",
                            }}
                        >
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                            >
                                <BlockIcon
                                    color="error"
                                    fontSize="small"
                                />
                                <Typography fontWeight={600}>
                                    {item.domain}
                                </Typography>
                            </Stack>
                            <Chip
                                label={item.status}
                                size="small"
                                color="default"
                            />
                        </Stack>
                    ))}
                </Paper>
                <Box mt={3}>
                    <Typography fontWeight={600}>
                        Requested For
                    </Typography>
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 1.5,
                            mt: 1,
                            bgcolor: "#fafafa",
                        }}
                    >
                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            {requestedBy}
                        </Typography>
                    </Paper>
                </Box>

                <Box mt={3}>
                    <Typography
                        fontWeight={600}
                        mb={1}
                    >
                        Reason For Removal *
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Provide the reason for removing these domains..."
                    />
                    <Typography
                        variant="caption"
                        color="text.secondary"
                    >
                        {reason.length} characters
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onClose}
                    sx={{
                        color: "#616161",
                        "&:hover": {
                            color: "#616161",
                            backgroundColor: "transparent",
                            boxShadow: "none",
                        },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disabled={!reason.trim() || loading}
                    onClick={submit}
                    sx={{
                        backgroundColor: "#000",
                        color: "#fff",
                        textTransform: "none",
                        "&:hover": {
                            backgroundColor: "#000",
                            color: "#fff",
                            boxShadow: "none",
                        },
                    }}
                >
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BlacklistDomainModal;